/**
 * PHASE 3 OPTIMIZATION - Lazy Loaded QuickView Modal
 * 
 * Lazy-loaded wrapper for QuickViewModal to reduce initial bundle size.
 * Only loads when first QuickView is triggered, reducing page load time.
 */

import { lazy, Suspense } from 'react';
import { DisplayProduct } from '@/types/productDisplay';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

// Lazy load the actual QuickViewModal component
const QuickViewModal = lazy(() => import('@/components/QuickViewModal'));

interface LazyQuickViewModalProps {
  product: DisplayProduct | any;
  isOpen: boolean;
  onClose: () => void;
  onViewProduct: () => void;
}

// Loading skeleton that matches QuickViewModal layout
const QuickViewSkeleton = () => (
  <Dialog open={true} onOpenChange={() => {}}>
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-1">
        {/* Left: Image skeleton */}
        <div className="space-y-3">
          <Skeleton className="w-full h-64 rounded-lg" />
        </div>
        
        {/* Right: Product info skeleton */}
        <div className="space-y-4">
          <div>
            <Skeleton className="h-8 w-3/4 mb-2" />
            <Skeleton className="h-6 w-1/2" />
          </div>
          <Skeleton className="h-1 w-full" />
          <Skeleton className="h-16 w-full" />
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-12 flex-1" />
            <Skeleton className="h-12 w-12" />
          </div>
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const LazyQuickViewModal = ({ product, isOpen, onClose, onViewProduct }: LazyQuickViewModalProps) => {
  console.log('🔍 LazyQuickViewModal: Rendered with isOpen:', isOpen, 'product:', product?.title);
  
  // Only render when open to prevent unnecessary loading
  if (!isOpen) {
    console.log('🔍 LazyQuickViewModal: Not rendering - modal closed');
    return null;
  }

  console.log('🔍 LazyQuickViewModal: Rendering Suspense with QuickViewModal');
  return (
    <Suspense fallback={<QuickViewSkeleton />}>
      <QuickViewModal
        product={product}
        isOpen={isOpen}
        onClose={onClose}
        onViewFullProduct={onViewProduct}
      />
    </Suspense>
  );
};

export default LazyQuickViewModal;